// One row in the audience builder. Discriminates on `filter.type` to render
// the right value editor (course multi-select, date, number range, yes/no,
// domain list). Changes propagate back via onChange({...filter, value}).

import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, SearchSelect, Textinput } from '../../../../ui';
import { AUDIENCE_FILTER, AUDIENCE_FILTER_SCHEMA } from '../../utils/audienceFilterSchema';
import { MOCK_COURSES_FOR_FILTER, MOCK_GROUPS_FOR_FILTER } from '../../mockData';

const RowEl = styled.div`
  display: grid;
  grid-template-columns: 180px auto 1fr 32px;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border: 1px solid ${({ theme }) => theme.colors.NEU_100};
  border-radius: 10px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'name remove'
      'op op'
      'value value';

    & > :nth-child(1) { grid-area: name; }
    & > :nth-child(2) { grid-area: op; }
    & > :nth-child(3) { grid-area: value; }
    & > :nth-child(4) { grid-area: remove; }
  }
`;

const FieldName = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.PRIMARY};
`;

const OpChip = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const RemoveButton = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.NEU_600};
  padding: 6px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover { background: ${({ theme }) => theme.colors.NEU_100}; }
`;

const RangeWrap = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  & > * { flex: 1 1 120px; min-width: 0; }
`;

const YesNoWrap = styled.div`
  display: inline-flex;
  gap: 6px;
`;

const YesNoButton = styled.button`
  background: ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_0)};
  color: ${({ $active, theme }) => ($active ? theme.colors.NEU_0 : theme.colors.NEU_PRIMARY)};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.PRIMARY : theme.colors.NEU_200)};
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;
`;

const courseOptions = MOCK_COURSES_FOR_FILTER.map((c) => ({ id: c.id, name: c.name }));
const groupOptions = MOCK_GROUPS_FOR_FILTER.map((g) => ({ id: g.id, name: g.name }));

const operatorLabel = (filter) => {
  const op = filter.operator;
  switch (op) {
    case 'in': return 'is any of';
    case 'eq': return 'equals';
    case 'lt': return 'before';
    case 'gt': return 'after';
    case 'gte': return 'at least';
    case 'lte': return 'at most';
    case 'between': return 'between';
    default: return op;
  }
};

const CourseMulti = ({ filter, onChange, id }) => {
  const selected = useMemo(
    () => courseOptions.filter((o) => (filter.value ?? []).includes(o.id)),
    [filter.value],
  );
  return (
    <SearchSelect
      id={id}
      options={courseOptions}
      selectedOptions={selected}
      setValue={(val) => onChange({ ...filter, value: (val ?? []).map((v) => v.id) })}
      multiple
    />
  );
};

const GroupMulti = ({ filter, onChange, id }) => {
  const selected = useMemo(
    () => groupOptions.filter((o) => (filter.value ?? []).includes(o.id)),
    [filter.value],
  );
  return (
    <SearchSelect
      id={id}
      options={groupOptions}
      selectedOptions={selected}
      setValue={(val) => onChange({ ...filter, value: (val ?? []).map((v) => v.id) })}
      multiple
    />
  );
};

const DomainList = ({ filter, onChange, id }) => {
  const value = (filter.value ?? []).map((d) => ({ id: d, name: d }));
  return (
    <SearchSelect
      id={id}
      options={value}
      selectedOptions={value}
      setValue={(val) =>
        onChange({
          ...filter,
          value: (val ?? []).map((v) => (v.inputValue ?? v.id ?? v.name).replace(/^@/, '').trim()).filter(Boolean),
        })
      }
      multiple
      creatable
    />
  );
};

const YesNo = ({ filter, onChange }) => (
  <YesNoWrap>
    <YesNoButton type='button' $active={filter.value === true} onClick={() => onChange({ ...filter, value: true })}>Yes</YesNoButton>
    <YesNoButton type='button' $active={filter.value === false} onClick={() => onChange({ ...filter, value: false })}>No</YesNoButton>
  </YesNoWrap>
);

const NumberRange = ({ filter, onChange }) => {
  const v = filter.value ?? {};
  return (
    <RangeWrap>
      <Textinput
        type='number'
        placeholder='min'
        value={v.min ?? ''}
        onChange={(e) => onChange({ ...filter, value: { ...v, min: e.target.value === '' ? null : Number(e.target.value) } })}
      />
      <span>—</span>
      <Textinput
        type='number'
        placeholder='max'
        value={v.max ?? ''}
        onChange={(e) => onChange({ ...filter, value: { ...v, max: e.target.value === '' ? null : Number(e.target.value) } })}
      />
    </RangeWrap>
  );
};

const PercentRange = ({ filter, onChange }) => {
  const v = filter.value ?? {};
  return (
    <RangeWrap>
      <Textinput
        type='number'
        min='0'
        max='100'
        placeholder='0%'
        value={v.min ?? ''}
        onChange={(e) => onChange({ ...filter, value: { ...v, min: e.target.value === '' ? null : Number(e.target.value) } })}
      />
      <span>—</span>
      <Textinput
        type='number'
        min='0'
        max='100'
        placeholder='100%'
        value={v.max ?? ''}
        onChange={(e) => onChange({ ...filter, value: { ...v, max: e.target.value === '' ? null : Number(e.target.value) } })}
      />
    </RangeWrap>
  );
};

const DateInput = ({ filter, onChange }) => (
  <Textinput
    type='date'
    value={filter.value ?? ''}
    onChange={(e) => onChange({ ...filter, value: e.target.value || null })}
  />
);

const RoleMulti = ({ filter, onChange }) => {
  // Static list — student-only per UX doc, but expose editor in case assistant
  // needs to target internal accounts (kept simple).
  const opts = useMemo(() => [
    { id: 'reader', name: 'Students' },
    { id: 'teacher', name: 'Teachers' },
  ], []);
  const selected = opts.filter((o) => (filter.value ?? []).includes(o.id));
  return (
    <SearchSelect
      options={opts}
      selectedOptions={selected}
      setValue={(val) => onChange({ ...filter, value: (val ?? []).map((v) => v.id) })}
      multiple
    />
  );
};

const EDITORS = {
  [AUDIENCE_FILTER.ENROLLED_IN]: CourseMulti,
  [AUDIENCE_FILTER.NOT_ENROLLED]: CourseMulti,
  [AUDIENCE_FILTER.COMPLETED_COURSE]: CourseMulti,
  [AUDIENCE_FILTER.COURSE_CATEGORY]: CourseMulti, // mocks don't have categories; reuse courses as placeholder
  [AUDIENCE_FILTER.GROUP]: GroupMulti,
  [AUDIENCE_FILTER.EMAIL_DOMAIN]: DomainList,
  [AUDIENCE_FILTER.HAS_PURCHASED]: YesNo,
  [AUDIENCE_FILTER.PURCHASE_AMOUNT]: NumberRange,
  [AUDIENCE_FILTER.COMPLETION_RATE]: PercentRange,
  [AUDIENCE_FILTER.LAST_ACTIVE_BEFORE]: DateInput,
  [AUDIENCE_FILTER.LAST_ACTIVE_AFTER]: DateInput,
  [AUDIENCE_FILTER.ROLE]: RoleMulti,
};

const FilterRow = ({ filter, onChange, onRemove }) => {
  const { t } = useTranslation();
  const schema = AUDIENCE_FILTER_SCHEMA[filter.type];
  const Editor = EDITORS[filter.type];

  const handleChange = useCallback((next) => onChange(next), [onChange]);

  return (
    <RowEl>
      <FieldName>{schema?.label ?? filter.type}</FieldName>
      <OpChip>{operatorLabel(filter)}</OpChip>
      <div>{Editor ? <Editor filter={filter} onChange={handleChange} id={`filter-${filter.id}`} /> : null}</div>
      <RemoveButton onClick={onRemove} aria-label={t('globals.remove', 'Remove')}>
        <Icon.Close size='small' />
      </RemoveButton>
    </RowEl>
  );
};

export default FilterRow;
